import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { dashboard } from 'src/app/models';
import { getDashboardsState, getUserInfoState, State } from '../../state';
import { loadDashboardById, updateDashboardSettings } from '../../state/actions';
import { DashboardService } from 'src/app/services/services/dashboard.service';
import { DialogOwnerChange } from 'src/app/common/dialogs/dialog-owner-change/dialog-owner-change.component';
import * as actions from '../../state/actions';
import { isDashboardEditable } from '../../dashboard-helpers/helpers';

@Component({
    selector: 'dashboard-filter',
    templateUrl: 'dashboard-filter.component.html',
    styleUrls: ['./dashboard-filter.component.scss'],
    standalone: false
})
export class DashboardFilterComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();
  dashboards$: Observable<dashboard[]>;
  dashboards: dashboard[] = [];
  initialDashboards: dashboard[] = [];
  initDashboards: dashboard[] = [];
  editedDashboardOwners: number[] = [];
  dsWithOwner: Map<number, number> = new Map();
  isDirty: boolean = false;
  visibleCount: number = 0;
  publicCount: number = 0;
  filter: string = '';

  allowToEditOtherUsersDashboards: boolean = false;
  isReadOnly: boolean = false;
  isUserRootAccess: boolean;
  isUserPartner: boolean;
  isUserAllowedToEditOtherUsersDashboards: boolean;
  userInfo$: Observable<any>;
  owners: any[] = [];

  get favoritesCount(): number {
    return this.dashboards?.filter(d => d.isFavorite).length ?? 0;
  }

  constructor(
    private DashboardsService: DashboardService,
    private store: Store<State>,
    public dialogRef: MatDialogRef<DashboardFilterComponent>, public dialogOwner: MatDialog, @Inject(MAT_DIALOG_DATA) public data: any) {
    this.dashboards$ = this.store.select(getDashboardsState);
    this.dashboards$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(d => {
      this.dashboards = JSON.parse(JSON.stringify(d));
      this.initialDashboards = JSON.parse(JSON.stringify(d));
      this.visibleCount = this.dashboards.filter(d => d.userOwned || d.visible).length;
      this.publicCount = this.dashboards.filter(d => !d.userOwned).length;
    });

    this.userInfo$ = this.store.select(getUserInfoState);
    this.userInfo$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(info => {
      this.isUserRootAccess = info?.userProperties?.isRoot;
      this.isUserPartner = info?.userProperties?.isPartner;
      this.isUserAllowedToEditOtherUsersDashboards = info?.userProperties?.allowToEditOtherUsersDashboards;
      this.isReadOnly = info?.userProperties?.isDashboardsReadOnly;
    });
  }

  ngOnInit(): void {
    this.allowToEditOtherUsersDashboards = this.data.allowToEditOtherUsersDashboards;
    this.isReadOnly = this.data.isReadOnly;
    this.owners = this.data.owners;
    this.initDashboards = this.dashboards;
    // this.DashboardsService.getAllAgents().subscribe(res => {
    //   this.owners = res;
    // })
  }

  @HostListener('window:keyup.esc') onKeyUp() {
    this.dialogRef.close();
  }

  mySettings(): dashboard[] {
    return this.dashboards
      .filter(ds => (ds.userOwned || ds.visible) && (ds.name.toLowerCase().includes(this.filter.toLowerCase()) || ds.ownerName.toLowerCase().includes(this.filter.toLowerCase())))
      .sort((a, b) => a.orderNo > b.orderNo ? 1 : a.orderNo === b.orderNo ? 0 : -1);
  }

  publicSettings(): dashboard[] {
    return this.dashboards.filter(ds => !ds.userOwned && (ds.name.toLowerCase().includes(this.filter.toLowerCase()) || ds.ownerName.toLowerCase().includes(this.filter.toLowerCase())));
  }

  favoritesSettings(): dashboard[] {
    return this.dashboards.filter(ds => ds.isFavorite && (ds.name.toLowerCase().includes(this.filter.toLowerCase()) || ds.ownerName.toLowerCase().includes(this.filter.toLowerCase())));
  }

  mySettingsCount(): number {
    return (this.dashboards.filter(ds => (ds.userOwned || ds.visible)) ?? []).length;
  }

  dashboardSettingChange(ds: dashboard) {
    if (this.visibleCount == 1 && ds.visible) {
      return;
    }
    ds.visible = !ds.visible;
    if (!ds.userOwned && !ds.visible) {
      ds.orderNo = 99999;
    }
    this.setDirty();
    this.visibleCount = this.dashboards.filter(d => d.visible).length;
  }

  setDirty() {
    this.isDirty = false;
    this.initialDashboards.forEach(id => {
      let d = this.dashboards.find(d => d.id == id.id)
      if (d.isFavorite != id.isFavorite || d.visible != id.visible || d.orderNo != id.orderNo) {
        this.isDirty = true;
      }
    });

    if (this.editedDashboardOwners.length > 0) {
      this.isDirty = true;
    }
  }

  drop(event: CdkDragDrop<dashboard[]>) {
    if (event.previousIndex == event.currentIndex) return;
    let ds = this.mySettings();
    moveItemInArray(ds, event.previousIndex, event.currentIndex);
    for (let index = 0; index < ds.length; index++) {
      const element = ds[index];
      this.dashboards.find(s => s.id == element.id).orderNo = index + 1;
    }
    this.dashboards.filter(s => !s.visible && !s.userOwned).map(s => s.orderNo = 99999);
    this.setDirty();
  }

  changeOwner(ds) {
    const dialogRef = this.dialogOwner.open(DialogOwnerChange, {
      panelClass: 'dialog-standard-pannel',
      backdropClass: "backdrop-invisible",
      data: {
        dashboardInfo: ds,
        owners: this.owners
      },
      width: "495px",
      height: "280px"
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data) {
        let ownerId = data.ownerId;
        let ownerName = data.owner;
        ds.newUserId = ownerId;
        ds.ownerName = ownerName;
        let index = this.editedDashboardOwners.indexOf(ds.id);
        if (index > -1) {
          let initOwner = this.initDashboards.find(d => d.id === ds.id).ownerName;
          let editedOwner = ownerName;
          if (initOwner === editedOwner) {
            this.editedDashboardOwners.splice(index, 1);
          }
        } else {
          this.editedDashboardOwners.push(ds.id);
          this.dsWithOwner.set(ds.id, ownerId);
        }

        this.setDirty();
      }
    });
  }

  canTransferOwnership(ds) {
    let isVisible = false;
    if (this.isUserAllowedToEditOtherUsersDashboards) {
      isVisible = true;
    } else if (!ds.isPublic) {
      isVisible = false;
    } else if (ds.userOwned) {
      isVisible = true;
    } else if (isDashboardEditable(ds, this.isUserRootAccess, this.isUserPartner, this.isUserAllowedToEditOtherUsersDashboards, this.isReadOnly)) {
      isVisible = true;
    }

    return isVisible
  }

  onCancelClick(): void {
    for (let i = 0; i < this.editedDashboardOwners.length; i++) {
      let ds = this.initDashboards.find(d => d.id === this.editedDashboardOwners[i]);
    }
    this.dialogRef.close();
  }

  onApplyClick(): void {
    for (let i = 0; i < this.editedDashboardOwners.length; i++) {
      let ds = this.dashboards.find(d => d.id === this.editedDashboardOwners[i]);
      ds.newUserId = this.dsWithOwner.get(this.editedDashboardOwners[i]);
      ds.isOwnerUpdated = true;
    }
    let idsForLoad = this.dashboards.filter(d => !d.userOwned && d.visible && !this.initialDashboards.find(i => i.id == d.id).visible).map(d => d.id);
    if (idsForLoad != null && idsForLoad.length > 0) {
      this.store.dispatch(loadDashboardById({ dashboarIds: idsForLoad }))
    }
    this.store.dispatch(updateDashboardSettings({ dashboars: this.dashboards }))
    this.dialogRef.close();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
